import * as Sentry from '@sentry/vue'

const DELIVERY_PRIORITY = ['COURIER', 'PICK_POINT', 'POST']

// Определяет, что сохранённый в профиле сервис доставки отсутствует среди
// сервисов, пришедших в ответе /delivery. Если LAST_CHOICES.SERVICE.ID нет
// или deliveryTypes ещё не загружены — возвращает false (не считаем битым).
// Проверка применима только когда город профиля совпадает с текущим:
// при смене города /delivery естественно возвращает другой набор сервисов,
// и применять fallback по приоритету в этом случае не нужно.
export function isProfileServiceMissing(profile, deliveryTypes, currentCity) {
  const lastServiceId = profile?.LAST_CHOICES?.LAST_DELIVERY_TYPE?.SERVICE?.ID
  if (!lastServiceId) return false
  if (!Array.isArray(deliveryTypes) || deliveryTypes.length === 0) return false
  const profileCity = profile?.ADDRESS_INFO_FIELDS?.CITY
  if (profileCity && currentCity && profileCity !== currentCity) return false
  return !deliveryTypes.some((dt) =>
    dt.ITEMS?.some((item) => item.ID === lastServiceId)
  )
}

// Подбирает запасной тип/сервис доставки, когда сохранённый в профиле сервис
// отсутствует среди ITEMS deliveryTypes (определяется isProfileServiceMissing).
//
// Возвращает { deliveryType, service } либо null, если подходящего варианта нет.
// service === null означает, что конкретную службу/ПВЗ должен выбрать пользователь.
//
// Правила (приоритет типов: COURIER > PICK_POINT > POST):
//  1. Исходный тип доступен и не PICK_POINT — автовыбор первой доступной службы.
//  2. Исходный тип доступен и PICK_POINT — тип сохраняем, ПВЗ не выбираем.
//  3. Исходного типа нет — берём следующий доступный по приоритету без выбора службы.
export function resolveAvailableDelivery(profile, deliveryTypes) {
  if (!profile || !Array.isArray(deliveryTypes) || deliveryTypes.length === 0) {
    return null
  }

  const originalType =
    profile.LAST_CHOICES?.LAST_DELIVERY_TYPE?.ID ||
    profile.ADDRESS_INFO_FIELDS?.DELIVERY_TYPE

  const hasItems = (type) =>
    type && Array.isArray(type.ITEMS) && type.ITEMS.length > 0

  const sameType = deliveryTypes.find((type) => type.ID === originalType)
  if (hasItems(sameType)) {
    if (sameType.ID === 'PICK_POINT') {
      return { deliveryType: sameType, service: null }
    }
    return { deliveryType: sameType, service: sameType.ITEMS[0] }
  }

  for (const id of DELIVERY_PRIORITY) {
    if (id === originalType) continue
    const type = deliveryTypes.find((d) => d.ID === id)
    if (hasItems(type)) {
      return { deliveryType: type, service: null }
    }
  }

  return null
}

export function formatMoneyGlobal(value, countryCode, currency, options = {}) {
  return new Intl.NumberFormat(countryCode, {
    style: 'currency',
    currency: currency,
    currencyDisplay: countryCode === 'NO' ? 'code' : 'symbol',
    numberingSystem: 'latn',
    ...options,
  }).format(value)
}

export function getDeliveryTransitGlobal(profile, deliveryTypes, declOfNum, t) {
  let transit = undefined
  if (profile.LAST_CHOICES?.LAST_DELIVERY_TYPE) {
    transit =
      deliveryTypes
        .find((el) => el.ID === profile.ADDRESS_INFO_FIELDS.DELIVERY_TYPE)
        ?.ITEMS?.find(
          (el) =>
            el.ID === profile.LAST_CHOICES?.LAST_DELIVERY_TYPE?.SERVICE?.ID
        )?.TRANSIT ??
      deliveryTypes.find(
        (el) => el.ID === profile.ADDRESS_INFO_FIELDS.DELIVERY_TYPE
      )?.ITEMS?.[0]?.TRANSIT
  } else {
    transit = deliveryTypes.find(
      (el) => el.ID === profile.ADDRESS_INFO_FIELDS.DELIVERY_TYPE
    )?.ITEMS?.[0]?.TRANSIT
  }

  return transit ? transit + ' ' + declOfNum(transit, t('declDays')) : ''
}

export function declOfNumGlobal(number, titles) {
  if (/[^0-9]$/.test(number)) return ''
  else {
    const cases = [2, 0, 1, 1, 1, 2]
    try {
      if (number.includes('-')) number = Number(number.split('-')[1])
      return titles[
        number % 100 > 4 && number % 100 < 20
          ? 2
          : cases[number % 10 < 5 ? number % 10 : 5]
      ]
    } catch (err) {
      Sentry.captureException(JSON.stringify(err) + 'NUMBER: ' + number)
      return ''
    }
  }
}

export function checkMaxLength(event, field, errorMessages, t, model) {
  if (!field.max && !field.min) return
  let value = event.target.value

  // number-поля (напр. POSTINDEX) — только цифры: отсекаем «-», «e», «.», «+»,
  // чтобы отрицательное «-235» не считалось валидными 4 символами и не сохранялось.
  if (field.type === 'number') {
    const digitsOnly = value.replace(/\D/g, '')
    if (digitsOnly !== value) {
      value = digitsOnly
      model[field.name] = value
    }
  }

  if (field.max && value.length > field.max) {
    model[field.name] = value.slice(0, field.max)
    errorMessages[field.name] = t('Character_limit_exceeded')
    return
  }

  if (field.min && value.length < field.min) {
    errorMessages[field.name] = t('Character_minimum_required')
    return
  }

  delete errorMessages[field.name]
}
